import { useState } from 'react';
import { Modal, Rate, TimePicker } from 'antd';
import dayjs from 'dayjs';
import Button from '../../reusable/Button/Button';
import Input from '../../reusable/Input/Input';
import Select from '../../reusable/Select/Select';
import { toast } from '../../reusable/Toast/toastStore';
import { useCreateTradingJournalMutation } from '../../store/api/trademateApi';
import { t } from '../../i18n/i18n';
import './CreateJournalDialog.scss';

const TRADE_TYPES = [
  { value: 'Buy', label: 'Buy' },
  { value: 'Sell', label: 'Sell' },
];
const ENTRIES = [
  { value: 'In', label: 'In' },
  { value: 'Out', label: 'Out' },
];

/**
 * Live's starting values (`CreateTrader.jsx` on main), keyed on the API's own
 * field names — `exit_traget` included, as the API spells it. The yes/no
 * questions start unanswered (null), the self-ratings at one star.
 */
const EMPTY = {
  date: '',
  symbol: '',
  trade_type: 'Buy',
  entry: 'In',
  profit: '',
  counter_trend: null,
  trigger: '',
  supporting_evidence: '',
  opposing_evidence: '',
  heads_up: '',
  notes: '',
  order_price: '',
  max_stop_price: '',
  size: '',
  max_loss: '',
  technical_stop: '',
  exit_traget: '',
  time_stop: null,
  risk_or_reward: '',
  alerts_placed: null,
  system_rules: 1,
  result_in_fear: 1,
  greed_or_pride: 1,
};

/** A yes/no question that can also be left unanswered, as live's radios can. */
function YesNo({ label, value, onChange }) {
  return (
    <div className="tj-yesno" role="radiogroup" aria-label={t(label)}>
      <span className="tj-yesno__label">{t(label)}</span>
      {[
        [true, 'Yes'],
        [false, 'No'],
      ].map(([answer, text]) => (
        <button
          key={text}
          type="button"
          role="radio"
          aria-checked={value === answer}
          className={`tj-yesno__option${value === answer ? ' is-on' : ''}`}
          onClick={() => onChange(value === answer ? null : answer)}
        >
          {t(text)}
        </button>
      ))}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="tj-section">
      <h3 className="tj-section__title">{t(title)}</h3>
      <div className="tj-section__grid">{children}</div>
    </section>
  );
}

/**
 * "Create Trade Journal" — N76, live's `+ Add Trade` form (Account Metrics →
 * Trading History on live, Trade History here). The same fields in the same
 * order, posted to the same endpoint; the entry then shows in Trademate's
 * Trade Journal. Live's Notes field is switched off in its form, so it is
 * here too — the entry goes up with `notes` empty, as live's does.
 *
 * Two differences from live, both about what reaches the API: the yes/no
 * answers go up as booleans (what the journal returns them as) rather than
 * the strings "Yes"/"No", and Date and Symbol are required — live's form
 * posts without them, and the entry it makes has nothing to list it by.
 */
export default function CreateJournalDialog({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [create, { isLoading }] = useCreateTradingJournalMutation();

  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));
  /** For the shared Input/Select, which report `{ target: { value } }`. */
  const field = (key) => (event) => set(key)(event.target.value);

  const ready = Boolean(form.date && form.symbol.trim());

  const close = () => {
    setForm(EMPTY);
    onClose();
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!ready) return;
    try {
      await create({ ...form, symbol: form.symbol.trim() }).unwrap();
      toast.success(t('Journal created'), t('It’s in your Trade Journal on Trademate.'));
      close();
    } catch (error) {
      toast.error(t('Could not create journal'), error?.data?.detail ?? error?.data?.message);
    }
  };

  const text = (key, label, props = {}) => (
    <Input label={t(label)} value={form[key]} onChange={field(key)} variant="form" {...props} />
  );

  return (
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      centered
      width={760}
      destroyOnHidden
      className="tj-dialog"
      rootClassName="tj-dialog-root"
      aria-label={t('Create trade journal')}
    >
      <form className="tj-dialog__body" onSubmit={submit}>
        <h2 className="tj-dialog__title">{t('Create Trade Journal')}</h2>

        <div className="tj-dialog__scroll">
          <Section title="Trade">
            {text('date', 'Date', { type: 'date', required: true })}
            {text('symbol', 'Symbol', { required: true, placeholder: t('e.g. EURUSD') })}
            <Select
              label={t('Trade type')}
              value={form.trade_type}
              onChange={field('trade_type')}
              options={TRADE_TYPES.map((o) => ({ ...o, label: t(o.label) }))}
              variant="form"
            />
            {text('profit', 'Profit', { type: 'number', step: 'any' })}
            <Select
              label={t('Entry')}
              value={form.entry}
              onChange={field('entry')}
              options={ENTRIES.map((o) => ({ ...o, label: t(o.label) }))}
              variant="form"
            />
          </Section>

          <YesNo
            label="Is this a counter trend trade?"
            value={form.counter_trend}
            onChange={set('counter_trend')}
          />

          <Section title="Setup">
            {text('trigger', 'Trigger')}
            {text('supporting_evidence', 'Supporting evidence')}
            {text('opposing_evidence', 'Opposing evidence')}
            <div className="tj-section__wide">{text('heads_up', 'Heads up')}</div>
          </Section>

          <Section title="Entry plan">
            {text('order_price', 'Entry order price', { type: 'number', step: 'any' })}
            {text('max_stop_price', 'Max stop price', { type: 'number', step: 'any' })}
            {text('size', 'Size', { type: 'number', step: 'any' })}
            {text('max_loss', 'Max $ loss', { type: 'number', step: 'any' })}
          </Section>

          <Section title="Exit plan">
            {text('technical_stop', 'Technical stop')}
            {text('exit_traget', 'Exit target / trigger')}
            <div className="reusable-input-group reusable-input-group--form">
              <label className="reusable-input-label">{t('Time stop')}</label>
              {/* 24-hour "HH:mm", the format live sends. */}
              <TimePicker
                format="HH:mm"
                value={form.time_stop ? dayjs(form.time_stop, 'HH:mm') : null}
                onChange={(time) => set('time_stop')(time ? time.format('HH:mm') : null)}
                className="reusable-antd-datepicker reusable-antd-datepicker--form"
                popupClassName="reusable-antd-datepicker-popup"
                placeholder={t('Select time')}
                needConfirm={false}
              />
            </div>
            {text('risk_or_reward', 'Est. risk / reward')}
          </Section>

          <YesNo label="Orders / alerts placed?" value={form.alerts_placed} onChange={set('alerts_placed')} />

          <section className="tj-section">
            <h3 className="tj-section__title">{t('Rate yourself')}</h3>
            <div className="tj-ratings">
              {[
                ['system_rules', 'Follows system rules?'],
                ['result_in_fear', 'Result in fear / anxiety?'],
                ['greed_or_pride', 'Spark ‘greed’ or ‘pride’?'],
              ].map(([key, label]) => (
                <div key={key} className="tj-ratings__row">
                  <span>{t(label)}</span>
                  {/* Live's ratings can't be cleared below one star. */}
                  <Rate value={form[key]} onChange={(v) => set(key)(v || 1)} className="tj-rate" />
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="tj-dialog__actions">
          <Button variant="toolbar" height={36} onClick={close} disabled={isLoading}>
            {t('Cancel')}
          </Button>
          <Button variant="submit" htmlType="submit" disabled={!ready} loading={isLoading}>
            {t('Create Trade Journal')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
